import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { GoogleMap } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../../lib/googleMaps';
import AdvancedMarker from '../../components/maps/AdvancedMarker';
import { Plus, PencilSimple, Power, MapPin } from '@phosphor-icons/react';
import ExportButton from '../../components/ExportButton';
import SuperAdminDeleteButton from '../../components/SuperAdminDeleteButton';

const SUPPLIER_EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'address', label: 'Address' },
  { key: 'contact', label: 'Contact' },
  { key: 'lat', label: 'Latitude' },
  { key: 'lng', label: 'Longitude' },
  { key: 'status', label: 'Status', format: r => r.status || 'active' },
  { key: 'created_at', label: 'Created At' },
];

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
];

function MapPicker({ lat, lng, onChange }) {
  const { isLoaded } = useGoogleMaps();
  const [mapType, setMapType] = useState('roadmap');

  const center = { lat: lat || 13.0827, lng: lng || 80.2707 };

  const handleClick = useCallback((e) => {
    onChange({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [onChange]);

  if (!isLoaded) return <div className="h-[250px] bg-[#F3F3F3] flex items-center justify-center text-[#666] text-xs">Loading map...</div>;

  return (
    <div data-testid="supplier-map-picker">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-[#666]">Click on the map to set the supplier location</p>
        <div className="flex border border-[#E5E5E5] text-[10px] uppercase tracking-widest">
          {['roadmap', 'satellite', 'hybrid'].map(t => (
            <button
              type="button"
              key={t}
              onClick={() => setMapType(t)}
              className={`px-2 py-1 ${mapType === t ? 'bg-[#002FA7] text-white' : 'bg-white text-[#666] hover:bg-[#F3F3F3]'}`}
              data-testid={`map-type-${t}`}
            >
              {t === 'roadmap' ? 'Map' : t === 'satellite' ? 'Sat' : 'Hybrid'}
            </button>
          ))}
        </div>
      </div>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '250px' }}
        center={center}
        zoom={12}
        onClick={handleClick}
        mapTypeId={mapType}
        options={{ styles: mapType === 'roadmap' ? MAP_STYLES : undefined, disableDefaultUI: true, zoomControl: true, mapTypeControl: false, mapId: GOOGLE_MAPS_MAP_ID }}
      >
        {(lat && lng) && (
          <AdvancedMarker
            position={{ lat, lng }}
            contentHTML={`
              <div style="transform:translate(-50%,-100%);filter:drop-shadow(0 2px 2px rgba(0,0,0,.35))">
                <svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z" fill="#FF3333" stroke="#fff" stroke-width="2"/>
                  <circle cx="15" cy="15" r="5" fill="#fff"/>
                </svg>
              </div>
            `}
          />
        )}
      </GoogleMap>
    </div>
  );
}

export default function SupplierManagement() {
  const [suppliers, setSuppliers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', contact: '', lat: 0, lng: 0 });
  const [showMap, setShowMap] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchSuppliers(); }, []);
  const fetchSuppliers = async () => { try { const { data } = await api.get('/admin/suppliers'); setSuppliers(data); } catch {} };
  const resetForm = () => { setForm({ name: '', address: '', contact: '', lat: 0, lng: 0 }); setEditing(null); setShowMap(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/admin/suppliers/${editing.id}`, form); }
      else { await api.post('/admin/suppliers', form); }
      fetchSuppliers(); setDialogOpen(false); resetForm();
    } catch {}
  };

  const handleToggle = async (id) => {
    await api.put(`/admin/toggle-status/suppliers/${id}`);
    fetchSuppliers();
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? suppliers.filter(s =>
        (s.name || '').toLowerCase().includes(q) ||
        (s.contact || '').toLowerCase().includes(q)
      )
    : suppliers;
  const MAX_ROWS = 200;
  const visible = filtered.slice(0, MAX_ROWS);

  return (
    <div data-testid="supplier-management" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Suppliers</h1>
          <p className="text-[#666] text-sm mt-1">
            {q ? `${filtered.length} match of ${suppliers.length}` : `${suppliers.length} suppliers`}
            {filtered.length > MAX_ROWS && ` · showing first ${MAX_ROWS}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton rows={filtered} columns={SUPPLIER_EXPORT_COLUMNS} filename="suppliers" testId="export-suppliers-btn" />
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-supplier-btn" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" onClick={resetForm}>
              <Plus size={16} className="mr-2" /> Add Supplier
            </Button>
          </DialogTrigger>          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-bold tracking-tight">{editing ? 'Edit' : 'Add'} Supplier</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div><Label className="text-xs uppercase tracking-widest">Name *</Label><Input data-testid="supplier-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-widest">Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="rounded-none" /></div>
              <div><Label className="text-xs uppercase tracking-widest">Contact</Label><Input value={form.contact} onChange={e => setForm({...form, contact: e.target.value})} className="rounded-none" /></div>

              {/* Location: manual or map */}
              <div className="border border-[#E5E5E5] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-widest font-bold">Location</Label>
                  <button
                    type="button"
                    onClick={() => setShowMap(!showMap)}
                    className="flex items-center gap-1 text-xs text-[#002FA7] hover:underline"
                    data-testid="toggle-map-picker-btn"
                  >
                    <MapPin size={14} /> {showMap ? 'Hide Map' : 'Pick from Map'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="text-xs uppercase tracking-widest">Latitude</Label><Input type="number" step="any" value={form.lat} onChange={e => setForm({...form, lat: parseFloat(e.target.value) || 0})} className="rounded-none" /></div>
                  <div><Label className="text-xs uppercase tracking-widest">Longitude</Label><Input type="number" step="any" value={form.lng} onChange={e => setForm({...form, lng: parseFloat(e.target.value) || 0})} className="rounded-none" /></div>
                </div>
                {showMap && (
                  <MapPicker
                    lat={form.lat}
                    lng={form.lng}
                    onChange={({ lat, lng }) => setForm(f => ({ ...f, lat: Math.round(lat * 1000000) / 1000000, lng: Math.round(lng * 1000000) / 1000000 }))}
                  />
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="rounded-none">Cancel</Button>
                <Button data-testid="supplier-save-btn" type="submit" className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none">{editing ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <div className="bg-white border border-[#E5E5E5] p-3">
        <Input
          data-testid="supplier-search-input"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or mobile..."
          className="rounded-none"
        />
      </div>
      <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-[#F3F3F3]">
            <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Address</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Contact</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Location</TableHead>
            <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.map((s, i) => (
              <TableRow key={s.id} className={i % 2 === 0 ? '' : 'bg-[#F3F3F3]/30'}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>{s.address}</TableCell>
                <TableCell>{s.contact}</TableCell>
                <TableCell className="text-sm text-[#666]">{s.lat && s.lng ? `${s.lat}, ${s.lng}` : '-'}</TableCell>
                <TableCell>
                  <span className={`text-xs uppercase tracking-widest px-2 py-1 ${(s.status || 'active') === 'active' ? 'bg-[#00C853]/10 text-[#00C853]' : 'bg-[#FF3333]/10 text-[#FF3333]'}`}>
                    {s.status || 'active'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <button onClick={() => { setEditing(s); setForm({ name: s.name, address: s.address || '', contact: s.contact || '', lat: s.lat || 0, lng: s.lng || 0 }); setDialogOpen(true); }} className="p-2 hover:bg-[#F3F3F3]" data-testid={`edit-supplier-${s.id}`}><PencilSimple size={16} /></button>
                  <button onClick={() => handleToggle(s.id)} className={`p-2 transition-colors ${(s.status || 'active') === 'active' ? 'hover:bg-[#FF3333]/10 text-[#FF3333]' : 'hover:bg-[#00C853]/10 text-[#00C853]'}`} title={(s.status || 'active') === 'active' ? 'Deactivate' : 'Activate'} data-testid={`toggle-supplier-${s.id}`}><Power size={16} /></button>
                  <SuperAdminDeleteButton masterType="suppliers" recordId={s.id} recordName={s.name} onDeleted={fetchSuppliers} />
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-[#666] py-8">{q ? 'No suppliers match your search' : 'No suppliers'}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
